import { useEffect, useState } from 'react';
import { Season } from '@appTypes/ElPrixContextData';

/**
 * Tab selection for the range- and charge-test strips: the current tab, a setter for the strip,
 * and the deep-link listener. Both sections answer to `selectRangeTest` — the event predates the
 * charge-test strip and is dispatched once from ModelDetailPage for either kind, so the name is
 * narrower than its scope. `tabContexts` must be memoised; the listener re-binds on identity.
 */
export const useTestTabSelection = <T extends { key: string }>(tabContexts: T[]) => {
  const [selectedTab, setSelectedTab] = useState(tabContexts[0]);

  useEffect(() => {
    if (!tabContexts.some((tab) => tab.key === selectedTab?.key)) setSelectedTab(tabContexts[0]);
  }, [selectedTab?.key, tabContexts]);

  useEffect(() => {
    const handleSelectTest = (event: CustomEvent<{ testDate: string; season: Season }>) => {
      const tab = tabContexts.find((candidate) => candidate.key === `${event.detail.season}_${event.detail.testDate}`);
      if (tab) setSelectedTab(tab);
    };
    window.addEventListener('selectRangeTest', handleSelectTest as EventListener);
    return () => window.removeEventListener('selectRangeTest', handleSelectTest as EventListener);
  }, [tabContexts]);

  const selectTabByKey = (key: string) => {
    const tab = tabContexts.find((candidate) => candidate.key === key);
    if (tab) setSelectedTab(tab);
  };

  return { selectedTab, selectTabByKey };
};
